// Machine translation as the page reasons about it: which rows a draft may land on, the
// words for a unit, the request id the page names an attempt by, the cost it sums, and the
// text the confirmation shows for a plan. Pure, so node --test checks literals.

import type { MachinePlan, MachineProfileDraft, MachineSettings, MachineUnitKind, ProviderKind, SegmentRow } from '../engine/protocol.ts'

export const UNIT_KINDS: MachineUnitKind[] = ['sentence', 'segment', 'block']

export const UNIT_WORDS: Record<MachineUnitKind, string> = { sentence: 'sentence', segment: 'Segment', block: 'Block' }

/// Thai-Tools.com's model families, the API's pipeline presets. The engine's adapter holds the
/// model each names for each stage (crates/interplae-machine/src/adapters/translation_suite.rs);
/// a test there checks the two lists agree.
export const THAI_TOOLS_FAMILIES: { id: string; label: string }[] = [
  { id: 'gemini_flash', label: 'Gemini Flash' },
  { id: 'gemini_pro', label: 'Gemini Pro' },
  { id: 'openai', label: 'OpenAI' },
  { id: 'openai_mini', label: 'OpenAI Mini' },
]

/// The engine's rule (crates/interplae-project/src/machine.rs, MachineSettings::normalized):
/// each active id once, in order, and a primary among them, else the first active one.
export function normalizedSettings(s: MachineSettings): MachineSettings {
  const active = [...new Set(s.active)]
  const primary = s.primary !== null && active.includes(s.primary) ? s.primary : (active[0] ?? null)
  return { active, primary, unit: s.unit, onEntry: s.onEntry }
}

/// Whether a draft from `profile` stays in the draft list without filling the Segment: it is
/// active and not the primary.
export const holdsBack = (s: MachineSettings, profile: string): boolean => s.active.includes(profile) && s.primary !== profile

/// The settings after a profile row's Active checkbox. A profile made active joins the end of the
/// list; the primary unticked falls to the next active profile.
export function setActive(s: MachineSettings, id: string, on: boolean): MachineSettings {
  const active = on ? [...s.active, id] : s.active.filter(a => a !== id)
  return normalizedSettings({ ...s, active })
}

/// The settings after a profile row's Primary radio, which only an active row enables.
export function setPrimary(s: MachineSettings, id: string): MachineSettings {
  return s.active.includes(id) ? normalizedSettings({ ...s, primary: id }) : s
}

/// What the profile form's kind select offers. Google Translate is the text adapter in its
/// default format; the plain text engine is the same adapter in the `strings` format.
export type ProfilePreset = ProviderKind | 'google'

export const PRESETS: { id: ProfilePreset; word: string }[] = [
  { id: 'interplae', word: 'Interplae contract' },
  { id: 'translation-suite', word: 'Thai-Tools.com' },
  { id: 'chat', word: 'Chat completions' },
  { id: 'google', word: 'Google Translate' },
  { id: 'text', word: 'Plain text engine' },
  { id: 'anthropic', word: 'Anthropic (Claude)' },
  { id: 'fake', word: 'Fake, for the gates' },
]

export function presetOf(kind: ProviderKind, options: Record<string, unknown>): ProfilePreset {
  if (kind !== 'text') return kind
  return options.format === 'strings' ? 'text' : 'google'
}

type PresetFields = Pick<MachineProfileDraft, 'kind' | 'endpoint' | 'model' | 'options'>

/// The fields after the kind select. Thai-Tools.com and Google Translate each serve one API,
/// so neither carries an endpoint or a model over to or from another kind.
export function withPreset<T extends PresetFields>(form: T, preset: ProfilePreset): T {
  const was = presetOf(form.kind, form.options)
  if (was === preset) return form
  const { format: _format, ...options } = form.options
  const fixed = (p: ProfilePreset) => p === 'translation-suite' || p === 'google'
  const reset = fixed(preset) || fixed(was)
  return {
    ...form,
    kind: preset === 'google' ? 'text' : preset,
    endpoint: reset ? '' : form.endpoint,
    model: reset ? null : form.model,
    options: preset === 'text' ? { ...options, format: 'strings' } : options,
  }
}

/// A row the crate's machine_draft transition accepts: untouched, or holding an earlier
/// machine draft. Never a human's text, a memory prefill, or anything confirmed.
export function takesDraft(row: SegmentRow): boolean {
  return row.review === 'untouched' || (row.review === 'unreviewed' && row.origin?.kind === 'machine')
}

/// A request id in the design's form, `mt-9f1c2a1e-body-477`, made by the page so a cancel
/// can name the attempt before the engine answers.
export function requestIdFor(block: string): string {
  const bytes = crypto.getRandomValues(new Uint8Array(4))
  const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('')
  return `mt-${hex}-${block.replace('#', '-')}`
}

/// Dollars kept as whole micro-dollars so 1,276 calls add up exactly.
export const toMicro = (usd: number | null): number => (usd === null ? 0 : Math.round(usd * 1e6))

/// `$0.0031`, `$1.28`, `$0`. Four decimals under a dollar, two above.
export function formatCost(micro: number): string {
  if (micro === 0) return '$0'
  const usd = micro / 1e6
  return usd < 1 ? `$${usd.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}` : `$${usd.toFixed(2)}`
}

/// The profile's name out of the provider string a draft carries, which is the name, a slash and
/// the profile's fingerprint; the fingerprint stays under the card's details.
export function providerName(provider: string): string {
  const at = provider.lastIndexOf('/')
  return at < 0 ? provider : provider.slice(0, at)
}

/// The Segments a request writes, by number: "Segment 344", "Segments 344 to 349", or the numbers
/// listed when they are not in a run.
export function segmentsWords(ordinals: readonly number[]): string {
  const numbers = [...new Set(ordinals)].sort((a, b) => a - b).map(o => o + 1)
  if (numbers.length === 0) return 'no Segment'
  if (numbers.length === 1) return `Segment ${numbers[0]}`
  const run = numbers.every((n, i) => i === 0 || n === numbers[i - 1]! + 1)
  if (run) return `Segments ${numbers[0]} to ${numbers[numbers.length - 1]}`
  return `Segments ${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}`
}

export interface PlanSummary {
  units: number
  destinations: number
  context: number
  calls: number
  cached: number
  ineligible: number
}

export function summarise(plan: MachinePlan): PlanSummary {
  const out: PlanSummary = { units: 0, destinations: 0, context: 0, calls: 0, cached: 0, ineligible: 0 }
  for (const u of plan.units) {
    if (u.ineligible !== null) {
      out.ineligible += 1
      continue
    }
    out.units += 1
    out.destinations += u.destinations.length
    out.context += u.context.length
    if (u.cached) out.cached += 1
    else out.calls += u.calls
  }
  return out
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/// What the document run asks before it sends anything: the count of units and the Segments
/// they change, the calls and what a stored draft answers.
export function runSentence(plan: MachinePlan, profileName: string): string {
  const s = summarise(plan)
  const unit = UNIT_WORDS[plan.unit]
  const head = `Translate ${plural(s.units, unit)} with ${profileName}, the primary profile, writing a draft on ${plural(s.destinations, 'Segment')}`
  const context = s.context ? `, with ${plural(s.context, 'Segment')} sent only as context` : ''
  const cached = s.cached ? ` ${plural(s.cached, unit)} ${s.cached === 1 ? 'is' : 'are'} answered by a stored draft without a call.` : ''
  return `${head}${context}. ${plural(s.calls, 'call')} to the provider.${cached}`
}

/// One planned unit before it is sent: how many calls it makes and whether they are joint.
export function unitSentence(unit: MachinePlan['units'][number]): string {
  if (unit.ineligible !== null) return unit.ineligible
  if (unit.cached) return 'A stored draft answers without a call.'
  const pieces = plural(unit.pieces, 'piece')
  const dest = plural(unit.destinations.length, 'Segment')
  const context = unit.context.length ? `, ${plural(unit.context.length, 'Segment')} as context` : ''
  const calls = unit.joint ? plural(unit.calls, 'call') : `${unit.calls} independent calls, one piece at a time`
  return `${pieces} for ${dest}${context}; ${calls}.`
}

/// A profile form's fields a refusal can point at.
export type ProfileField = 'name' | 'endpoint' | 'options' | 'credential'

/// The field a refusal of a profile names, so the form marks it beside its own message
/// (docs/design/ux-review.md, unit 12); null when it names none. Options come first, since a
/// credential found in the options is repaired there.
export function refusedField(message: string): ProfileField | null {
  const m = message.toLowerCase()
  if (m.includes('option')) return 'options'
  if (m.includes('credential')) return 'credential'
  if (m.includes('endpoint') || m.includes('url')) return 'endpoint'
  if (m.includes('name')) return 'name'
  return null
}

const named = (names: string[]): string => (names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`)

/// What the profile form's Advanced expansion says while it is closed: how many options the
/// JSON as typed holds, by name, or that it is not JSON yet.
export function optionsSummary(text: string): string {
  let options: unknown
  try {
    options = JSON.parse(text || '{}')
  } catch {
    return 'options not JSON yet'
  }
  const keys = options && typeof options === 'object' && !Array.isArray(options) ? Object.keys(options).sort() : null
  if (keys === null) return 'options not an object'
  return keys.length ? `${plural(keys.length, 'option')}: ${named(keys)}` : 'no options'
}
